import { SettingUrls } from 'constants/SettingsUrls';
import { PackageProviderName, SettingKey, WizardStepName } from 'data/ppoints/constants';
import { activateAllShippingTypes } from 'features/settings/components/ppoints/pickup-points/components/pickup-point-settings/mutation/saveAcivatedShippingTypes';
import useWizard from 'features/wizard/hooks/useWizard';
import useErrorBanner from 'hooks/error-banner/useErrorBanner';
import { useUpdateIntercomFields } from 'hooks/intercom/useUpdateIntercomFields';
import usePlan from 'hooks/plan/usePlan';
import { useCountries } from 'hooks/useCountries';
import useSettings from 'hooks/useSettings';
import { ProviderToCountrySetting } from 'models/ppoints/settings/SettingsToEcom';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { getEnabledShippingTypes } from 'redux/slices/enabled-shipping-types/enabled-shipping-types.actions';

const useActivateProviders = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const wizard = useWizard();

    const { data: countries } = useCountries();
    const { settings: providerToCountry, setSettingsWithTypeReturn: setProviderToCountry } =
        useSettings<ProviderToCountrySetting>(SettingKey.provider_to_country);
    const { isMonthlyProviderLimitReached } = usePlan();
    const { setBanner, emptyBanner } = useErrorBanner();
    const { handleProviderToCountryToIntercom } = useUpdateIntercomFields();
    const packageProviders = useAppSelector((state) => state.packageProviders);

    const setSaveErrorBanner = useCallback(() => {
        setBanner({
            titleKey: t('SHIPPING_PROVIDER.ERROR.SAVE_ERROR.TITLE'),
            descriptionKey: t('SHIPPING_PROVIDER.ERROR.SAVE_ERROR.DESCRIPTION'),
            tone: 'warning',
            withSupportActions: true,
        });
    }, [setBanner, t]);

    const activateShippingTypesFor = useCallback(
        async (providers: PackageProviderName[]) => {
            const results = await Promise.allSettled(providers.map((provider) => activateAllShippingTypes(provider)));

            const failed = results.some(
                (result) => result.status === 'rejected' && result.reason?.response?.status !== 409
            );

            if (failed) {
                setBanner({
                    titleKey: t('SHIPPING_PROVIDER.ERROR.ACTIVATE_SHIPPING_TYPES_ERROR.TITLE'),
                    descriptionKey: t('SHIPPING_PROVIDER.ERROR.ACTIVATE_SHIPPING_TYPES_ERROR.DESCRIPTION'),
                    tone: 'warning',
                    withSupportActions: true,
                });
            }

            await dispatch(getEnabledShippingTypes())
                .unwrap()
                .catch(() => {});
        },
        [dispatch, setBanner, t]
    );

    const activateProviders = useCallback(
        async (countryCode: string, providers: PackageProviderName[]): Promise<boolean> => {
            emptyBanner();

            const countryName = countries?.find((country) => country.country_code === countryCode)?.name;

            if (!countryName || providers.length === 0) {
                setSaveErrorBanner();
                return false;
            }

            const localSettings: ProviderToCountrySetting = { ...(providerToCountry ?? {}) };
            localSettings[countryName] = [...new Set([...(localSettings[countryName] ?? []), ...providers])];

            const activatedCount = new Set(Object.values(localSettings).flat()).size;

            if (isMonthlyProviderLimitReached(activatedCount)) {
                setBanner({
                    titleKey: t('PLAN_LIMIT.PLAN_LIMIT_REACHED'),
                    descriptionKey: t('PLAN_LIMIT.EXCEEDED_PROVIDER_LIMIT'),
                    tone: 'warning',
                    action: {
                        content: t('PLAN_LIMIT.UPGRADE_YOUR_PLAN'),
                        onAction: () => {
                            navigate(SettingUrls.billing);
                        },
                    },
                });
                return false;
            }

            const savedSettings = await setProviderToCountry(localSettings);

            if (!savedSettings) {
                setSaveErrorBanner();
                return false;
            }

            shopify.toast.show(
                providers.length > 1
                    ? t('SHIPPING_PROVIDER.TOASTS.PROVIDERS_ACTIVATED')
                    : t('SHIPPING_PROVIDER.TOASTS.PROVIDER_ACTIVATED')
            );

            handleProviderToCountryToIntercom(savedSettings, packageProviders.packageProviders);

            if (!wizard.isAllDone()) {
                await wizard.handleWizardStepper(WizardStepName.choose_service_provider);
            }

            await activateShippingTypesFor(providers);

            return true;
        },
        [
            countries,
            providerToCountry,
            isMonthlyProviderLimitReached,
            setProviderToCountry,
            packageProviders.packageProviders,
            activateShippingTypesFor,
            setSaveErrorBanner,
            emptyBanner,
            setBanner,
            navigate,
            t,
        ]
    );

    return { activateProviders };
};

export default useActivateProviders;
